// @ai-generated 2026-07-21T03:45:01+00:00 by philipp.kammerer@synqony.com using claude-fable-5
import { useEffect, useMemo, useRef, useState } from 'react';
import { useUpsertNotificationSetting } from '@Hooks/Messages';
import { type ApiNotification, type EnumNotificationModule } from '@Model';
import { Button, FlexColumn, renderDateTime, Tooltip, useTranslate } from '@synqo/frontend-component-library';
import { IconBellOff, IconCircleCheck, IconDots, IconSettings } from '@tabler/icons-react';
import { describeApiMessage, parseApiMessages } from '@utils/parseApiMessages';
import { MessageLevelIcon } from '../../Messages/MessageLevelIcon';
import { getButtonVariantByMessageVariant, getVariantByMessageLevel } from '../../Messages/messageVariant';
import { useClientNavigate } from '../../../../Routing/useClientNavigate';
import { isMandatoryInApp, NOTIFICATION_CATALOG, notificationLinkPath } from './notificationCatalog';
import styles from './NotificationHubButton.module.css';

interface NotificationRowProps {
    notification: ApiNotification;
    onAcknowledge: (id: string) => Promise<unknown>;
    onOpenSettings: (module: EnumNotificationModule) => void;
}

export function NotificationRow({ notification, onAcknowledge, onOpenSettings }: NotificationRowProps) {
    const i18n = useTranslate('NotificationHub');
    const { mutateAsync: upsert } = useUpsertNotificationSetting();
    const navigate = useClientNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const isUnread = !notification.acknowledged_at;
    const linkPath = notificationLinkPath(notification);

    useEffect(() => {
        if (!menuOpen) {
            return;
        }
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!menuRef.current?.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, [menuOpen]);
    const catalogModule = NOTIFICATION_CATALOG.find((moduleEntry) => moduleEntry.module === notification.module);
    const canMute = catalogModule !== undefined && notification.key !== '' && !isMandatoryInApp(notification.key);
    const hasMenuOptions = isUnread || canMute || catalogModule !== undefined;

    const mute = () => {
        void upsert({
            module: notification.module,
            key: notification.key,
            in_app: false,
            email: false,
        });
    };
    const messages = useMemo(() => {
        try {
            return parseApiMessages(notification.payload);
        } catch {
            return [];
        }
    }, [notification.payload]);
    // The first message is the headline the notification was built around; anything after it says
    // why — a failed export sends one message per reported problem. One of them fits in a row, the
    // rest are counted, and all of them live on the record the row links to.
    const [headline, ...reasons] = messages;
    const [firstReason, ...furtherReasons] = reasons;
    const variant = headline ? getVariantByMessageLevel(headline.level) : 'info';

    const openRecord = () => {
        if (linkPath === undefined) {
            return;
        }
        if (isUnread) {
            void onAcknowledge(notification.id);
        }
        navigate(linkPath);
    };

    return (
        <div className={styles.row} data-unread={isUnread} data-variant={variant}>
            <MessageLevelIcon variant={variant} />
            <FlexColumn justify="start" align="stretch" gap="none" className={styles.content}>
                {headline?.title && <span className={styles.title}>{headline.title}</span>}
                {headline?.message && <span className={styles.message}>{headline.message}</span>}
                {firstReason && <span className={styles.reason}>{describeApiMessage(firstReason)}</span>}
                {furtherReasons.length > 0 && (
                    <span className={styles.furtherReasons}>
                        {furtherReasons.length === 1
                            ? `+1 ${i18n('weiterer Grund')}`
                            : `+${furtherReasons.length} ${i18n('weitere Gründe')}`}
                    </span>
                )}
                <span className={styles.time}>{renderDateTime(notification.created_at, 'datetime')}</span>
                {linkPath !== undefined && (
                    <span data-closes-popover className={styles.viewNow}>
                        <Button size="xs" variant={getButtonVariantByMessageVariant(variant)} onClick={openRecord}>
                            {i18n('Jetzt ansehen')}
                        </Button>
                    </span>
                )}
            </FlexColumn>
            <div className={styles.unreadDotColumn}>
                {isUnread && (
                    <Tooltip label={i18n('Als gelesen markieren')}>
                        <button
                            type="button"
                            aria-label={i18n('Als gelesen markieren')}
                            className={styles.unreadDot}
                            data-variant={variant}
                            onClick={(event) => {
                                event.stopPropagation();
                                void onAcknowledge(notification.id);
                            }}
                        />
                    </Tooltip>
                )}
            </div>
            <div className={styles.rowMenuColumn} ref={menuRef}>
                {hasMenuOptions && (
                    <Tooltip label={i18n('Optionen')}>
                        <button
                            type="button"
                            aria-label={i18n('Optionen')}
                            aria-expanded={menuOpen}
                            className={styles.rowMenuTrigger}
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            <IconDots size={16} />
                        </button>
                    </Tooltip>
                )}
                {menuOpen && (
                    <div className={styles.rowMenu} role="menu">
                        {isUnread && (
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.rowMenuItem}
                                onClick={() => {
                                    setMenuOpen(false);
                                    void onAcknowledge(notification.id);
                                }}
                            >
                                <IconCircleCheck size={14} />
                                {i18n('Als gelesen markieren')}
                            </button>
                        )}
                        {canMute && (
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.rowMenuItem}
                                onClick={() => {
                                    setMenuOpen(false);
                                    mute();
                                }}
                            >
                                <IconBellOff size={14} />
                                {i18n('Stummschalten')}
                            </button>
                        )}
                        {catalogModule && (
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.rowMenuItem}
                                onClick={() => {
                                    setMenuOpen(false);
                                    onOpenSettings(notification.module);
                                }}
                            >
                                <IconSettings size={14} />
                                {`${i18n('Einstellungen')}: ${i18n(catalogModule.labelDe)}`}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
